import React, { useCallback, useEffect, useRef } from 'react';
import { ChatMessage } from 'clients/ChatClient';
import MessageBubble from './MessageBubble';
import ToolIndicator from './ToolIndicator';
import styles from './MessageList.module.css';

interface MessageListProps {
    messages: ChatMessage[];
    isStreaming: boolean;
    activeTools: string[];
}

const SCROLL_THRESHOLD = 80;

const MessageList: React.FC<MessageListProps> = ({ messages, isStreaming, activeTools }) => {
    const listRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const userScrolledUp = useRef(false);

    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        userScrolledUp.current = distanceFromBottom > SCROLL_THRESHOLD;
    }, []);

    // Reset scroll flag when user sends a new message
    const lastMessage = messages[messages.length - 1];
    const prevLastMessageRef = useRef<ChatMessage | null>(null);

    useEffect(() => {
        // If a new user message was added, reset the scroll flag
        if (lastMessage?.role === 'user' && lastMessage !== prevLastMessageRef.current) {
            userScrolledUp.current = false;
        }
        prevLastMessageRef.current = lastMessage;

        if (!userScrolledUp.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'auto' });
        }
    }, [messages, activeTools, lastMessage]);

    const toolsActive = activeTools.length > 0;

    return (
        <div className={styles.list} ref={listRef} onScroll={handleScroll}>
            {messages.map((msg, idx) => {
                const isLast = idx === messages.length - 1;
                const hideEmpty = isLast && msg.role === 'assistant' && !msg.content && toolsActive;
                if (hideEmpty) return null;
                return (
                    <MessageBubble
                        key={idx}
                        message={msg}
                        isStreaming={isStreaming && isLast && msg.role === 'assistant'}
                    />
                );
            })}
            {toolsActive && <ToolIndicator tools={activeTools} />}
            <div ref={bottomRef} />
        </div>
    );
};

export default MessageList;
